import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  GroupOutlined as GroupIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { teamApi, adminApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { TeamMember, UserRole, UserGroup } from '@/types';

interface MembersTableProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const EMPTY_FORM = {
  email: '',
  display_name: '',
  role: 'member' as UserRole,
  google_user_id: '',
};

export default function MembersTable({ teamId, onNotify }: MembersTableProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TeamMember | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  // ── Queries ──────────────────────────────────────────────────────────

  const { data: members } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamApi.getMembers(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: linkedGroups } = useQuery({
    queryKey: ['team-linked-groups', teamId],
    queryFn: () => teamApi.getLinkedGroups(teamId).then((res) => res.data),
    enabled: !!teamId && isAdmin,
  });

  const { data: allGroups } = useQuery({
    queryKey: ['admin-groups-for-link'],
    queryFn: () => adminApi.listGroups({ limit: 100 }).then((res) => res.data.items),
    enabled: linkDialogOpen && isAdmin,
  });

  // ── Member mutations ─────────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: (data: Partial<TeamMember>) => teamApi.addMember(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      onNotify('Member added successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to add member: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TeamMember> }) =>
      teamApi.updateMember(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      onNotify('Member updated successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update member: ${getErrorMessage(error)}`, 'error');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => teamApi.removeMember(teamId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      onNotify('Member removed successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to remove member: ${getErrorMessage(error)}`, 'error');
    },
  });

  // ── Group link mutations ─────────────────────────────────────────────

  const linkMutation = useMutation({
    mutationFn: (groupId: string) => teamApi.linkGroup(teamId, groupId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-linked-groups', teamId] });
      const d = res.data;
      onNotify(`Group linked! ${d.members_added} added, ${d.members_skipped} skipped.`, 'success');
      setLinkDialogOpen(false);
    },
    onError: (error: Error) => {
      onNotify(`Failed to link group: ${getErrorMessage(error)}`, 'error');
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (groupId: string) => teamApi.unlinkGroup(teamId, groupId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-linked-groups', teamId] });
      const d = res.data;
      onNotify(`Group unlinked. ${d.members_removed} members removed.`, 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to unlink group: ${getErrorMessage(error)}`, 'error');
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleOpen = (item?: TeamMember) => {
    if (item) {
      setEditingItem(item);
      setForm({
        email: item.email,
        display_name: item.display_name || '',
        role: item.role || 'member',
        google_user_id: item.google_user_id || '',
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = () => {
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.id,
        data: {
          display_name: form.display_name || undefined,
          role: form.role,
        },
      });
    } else {
      addMutation.mutate({
        email: form.email,
        display_name: form.display_name || undefined,
        role: form.role,
        google_user_id: form.google_user_id || undefined,
      });
    }
  };

  const handleRemove = (memberId: string) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      removeMemberMutation.mutate(memberId);
    }
  };

  const handleUnlink = (groupId: string, groupName: string) => {
    if (window.confirm(`Unlink group "${groupName}"? Group-sourced members may be removed.`)) {
      unlinkMutation.mutate(groupId);
    }
  };

  const isSaving = addMutation.isPending || updateMutation.isPending;

  // Available groups = all groups minus already-linked ones
  const linkedGroupIds = new Set((linkedGroups || []).map((lg) => lg.group_id));
  const availableGroups: UserGroup[] = (allGroups || []).filter((g) => !linkedGroupIds.has(g.id));

  return (
    <>
      {/* Button bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {isAdmin && (
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => setLinkDialogOpen(true)}>
            Link Group
          </Button>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Member
        </Button>
      </Box>

      {/* Linked groups chips */}
      {linkedGroups && linkedGroups.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
            Linked groups:
          </Typography>
          {linkedGroups.map((lg) => (
            <Chip
              key={lg.id}
              icon={<GroupIcon />}
              label={lg.group_name}
              onDelete={isAdmin ? () => handleUnlink(lg.group_id, lg.group_name) : undefined}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      )}

      {/* Members table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members?.map((member) => (
              <TableRow key={member.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {member.display_name || '-'}
                    {member.added_via_group && (
                      <Tooltip title="Added via linked group">
                        <GroupIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  <Chip label={member.role} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.is_active ? 'Active' : 'Inactive'}
                    color={member.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit member">
                    <IconButton size="small" onClick={() => handleOpen(member)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {member.added_via_group ? (
                    <Tooltip title="Unlink group to remove">
                      <span>
                        <IconButton size="small" color="error" disabled>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Remove member">
                      <IconButton size="small" color="error" onClick={() => handleRemove(member.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {members?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No members added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Member dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Member' : 'Add Member'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
              disabled={!!editingItem}
            />
            <TextField
              label="Display Name"
              value={form.display_name}
              onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                label="Role"
                onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value as UserRole }))}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="team_lead">Team Lead</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {!editingItem && (
              <TextField
                label="Google User ID"
                value={form.google_user_id}
                onChange={(e) => setForm((prev) => ({ ...prev, google_user_id: e.target.value }))}
                fullWidth
                helperText="Optional. Used for Google Chat @mentions."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || (!editingItem && !form.email.trim())}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Group dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link User Group</DialogTitle>
        <DialogContent>
          {availableGroups.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {allGroups ? 'All groups are already linked to this team' : 'Loading groups...'}
            </Typography>
          ) : (
            <List disablePadding>
              {availableGroups.map((group) => (
                <ListItem
                  key={group.id}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => linkMutation.mutate(group.id)}
                      disabled={linkMutation.isPending}
                    >
                      Link
                    </Button>
                  }
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <ListItemText
                    primary={group.name}
                    secondary={`${group.member_count} members${group.description ? ` \u2014 ${group.description}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
